import { Component, ElementRef, HostListener, Injector, Input } from '@angular/core';
import {
  faArrowLeft,
  faArrowRight,
  faPlus,
  faPercentage,
  faPrint,
  faList,
  faPen,
  faUnlock,
  faSave,
  faPaperPlane,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import { DiscountTypeEnum } from '../../enum/discount-type.enum';
import { BaseTunnelObject } from '../../pages/tunnel/model/base-tunnel-object';
import { TunnelNavigationStore } from '../../stores/tunnel/tunnel-navigation.store';
import { MODAL, ModalService } from '../../services/modal.service';
import { CONFIG_CONTEXT } from '../../enum/config-context.enum';
import { CustomerService } from '../../services/customer.service';
import { KitchenConfigurationData } from '../../stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { FundingState } from '../../stores/tunnel/model/funding-state';
import { KitchenExtraPricing } from '../../stores/tunnel/model/kitchen-extra-pricing';
import { PricingState } from '../../stores/tunnel/model/pricing-state';
import { TunnelDataState } from '../../stores/tunnel/model/tunnel-data-state';
import { TranslateService } from '@ngx-translate/core';
import { KitchenService } from '../../services/configurators-2d/applications/kitchen.service';
import { PrintService } from '../../services/print.service';
import { ProcessQuoteService } from '../../services/process-quote.service';
import { CurrentService } from '../../services/current.service';
import { ProjectService } from '../../services/project.service';
import { WorkbenchService } from '../../services/configurators-2d/applications/workbench.service';
import { TunnelStep } from '../../pages/tunnel/model/tunnel-step-type';
import { combineLatest } from 'rxjs';
import { StorageService } from 'src/app/services/configurators-2d/applications/storage.service';
import { ToastService } from '../../services/toast.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { HBMConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/hbm-configuration-data';
import { HbmPdfViewModel } from '../../models/configurators-2d/tunnel/common-hbm-tunnel-dtos';
import { StorageConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/storage-configuration-data';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { BaseAppService } from 'src/app/services/configurators-2d/base-app-service';
import { Configuration2D } from 'src/app/models/configurators-2d/configuration2d';
import { BathroomConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/bathroom-configuration-data';
import { BathroomHbmConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/bathroom-hbm-configuration-data';
import { BathroomService } from 'src/app/services/configurators-2d/applications/bathroom.service';
import { BathroomHbmService } from 'src/app/services/configurators-2d/applications/bathroom-hbm.service';
import { CarpentryTunnelStore } from 'src/app/pages/tunnel/carpentry/carpentry-tunnel.store';
import { CarpentryService } from 'src/app/services/configurators-2d/applications/carpentry.service';
import { BasketService } from 'src/app/services/basket.service';
import { CarpentryConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/carpentry-configuration-data';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';
import { ServiceManager } from 'src/app/services/configurators-2d/service-manager';
import {
  hasCarpentryComPlusOrder,
  isCarpentryLockedBySapOrderNumber,
  isCarpentryProjectLocked,
} from 'src/app/services/helpers/carpentry-order-state.helper';
import { BomDataHelper } from 'src/app/services/helpers/bom-data-helper';

@Component({
  selector: 'ng-casto-action-bar',
  templateUrl: './action-bar.component.html',
})
export class ActionBarComponent extends BaseTunnelObject {
  protected flyerVisible: boolean = false;
  protected currentState: TunnelDataState;
  protected nextStep: TunnelStep;
  protected currentStep: TunnelStep;
  protected previousStep: TunnelStep;
  protected configuration: HBMConfigurationData;
  protected canPrint: boolean = false;
  protected finalStep: boolean = false;
  protected canNextStep: boolean = true;
  protected currentPricing: PricingState;
  private currentFunding: FundingState;

  protected printLoading: boolean = false;
  protected comPlusLoading: boolean;
  protected tunnelLoading: boolean = false;
  protected pricingLoading: boolean = false;
  protected unlockLoading: boolean = false;
  protected saveLoading: boolean = false;
  protected sendMailLoading: boolean = false;
  protected pdfMenuVisible = false;
  protected beforeNextProcessing = false;

  @Input() beforeNext: () => Promise<void>;
  @Input() beforeProductList: () => Promise<void>;
  @Input() specificPrintAction: () => Promise<void>;
  @Input() forceSpecificPrintActionEnabled: boolean = false;

  get bom() {
    return this.currentConfigurationData?.bom;
  }

  get isProductListDisabled(): boolean {
    if (this.currentStep?.skipCheckBomEmpty) {
      return false;
    }

    return this.isHbmProductListContext ? BomDataHelper.isBomUnavailableForProductList(this.bom) : !this.bom;
  }

  get currentConfigurationData() {
    switch (this.customerService.context) {
      case CONFIG_CONTEXT.KITCHEN:
        return this.currentState?.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;
      case CONFIG_CONTEXT.STORAGE:
        return this.currentState?.configurationData.get(CONFIG_CONTEXT.STORAGE) as StorageConfigurationData;
      case CONFIG_CONTEXT.BATHROOM_V2:
        return this.currentState?.configurationData.get(CONFIG_CONTEXT.BATHROOM_V2) as BathroomConfigurationData;
      case CONFIG_CONTEXT.BATHROOM:
        return this.currentState?.configurationData.get(CONFIG_CONTEXT.BATHROOM) as BathroomHbmConfigurationData;
      case CONFIG_CONTEXT.CARPENTRY:
        return this.currentState?.configurationData.get(CONFIG_CONTEXT.CARPENTRY) as CarpentryConfigurationData;
      default:
        return undefined;
    }
  }

  get currentDiscount() {
    return this.currentState?.currentDiscount;
  }

  get projectLocked() {
    if (this.isCarpentry) {
      return isCarpentryProjectLocked(this.currentConfigurationData);
    }

    return this.currentService.HBMProject?.locked ?? false;
  }

  get isPrevisitWorkbench(): boolean {
    return (
      (this.currentConfigurationData as KitchenConfigurationData)?.workbenchConfigurationId && //si un plan de travail est renseigné dans le projet
      (this.currentState.currentPricing.extraPricing as KitchenExtraPricing)?.totalInstallation?.actualAmount > 0 //Et qu'il y a un forfait de pose, alors c'est du cosentino et il faut faire une prévisite
    );
  }

  get isKitchen(): boolean {
    return this.customerService.context === CONFIG_CONTEXT.KITCHEN;
  }

  get isStorage(): boolean {
    return this.customerService.context === CONFIG_CONTEXT.STORAGE;
  }

  get isBathroomHbm(): boolean {
    return this.customerService.context === CONFIG_CONTEXT.BATHROOM;
  }

  private get isHbmProductListContext(): boolean {
    return this.isKitchen || this.isStorage || this.isBathroomHbm;
  }

  get isBathroomV2(): boolean {
    return this.customerService.context === CONFIG_CONTEXT.BATHROOM_V2;
  }

  get isCarpentry(): boolean {
    return this.customerService.context === CONFIG_CONTEXT.CARPENTRY;
  }

  constructor(
    protected tunnelNavigationStore: TunnelNavigationStore,
    protected tunnelDataStore: TunnelDataStore,
    protected modalService: ModalService,
    protected customerService: CustomerService,
    protected translateService: TranslateService,
    protected kitchenService: KitchenService,
    protected storageService: StorageService,
    protected bathroomService: BathroomService,
    protected bathroomHbmService: BathroomHbmService,
    protected workbenchService: WorkbenchService,
    protected printService: PrintService,
    protected readonly statisticService: StatisticService,
    protected currentService: CurrentService,
    protected projectService: ProjectService,
    protected processQuoteService: ProcessQuoteService,
    protected elementRef: ElementRef,
    protected toastService: ToastService,
    protected injector: Injector,
    private carpentryService: CarpentryService,
    private basketService: BasketService,
    private carpentryTunnelStore: CarpentryTunnelStore,
    private serviceManager: ServiceManager,
    protected featureSettingsService: FeatureSettingsService
  ) {
    super(tunnelNavigationStore, tunnelDataStore);
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentState().subscribe(state => {
        if (state) {
          this.canPrint = this.canTunnelPrint(state);
          this.currentState = state;
        }
      }),
      this.tunnelNavigationStore.getIsFinalStep().subscribe(isFinalStep => {
        this.finalStep = isFinalStep;
      }),
      this.tunnelNavigationStore.getCurrentStep().subscribe(currentStep => {
        if (currentStep) {
          this.currentStep = currentStep;
        }
      }),
      this.tunnelNavigationStore.getNextStep().subscribe(nextStep => {
        if (nextStep) {
          this.nextStep = nextStep;
        }
      }),
      this.tunnelNavigationStore.getPreviousStep().subscribe(previousStep => {
        if (previousStep) {
          this.previousStep = previousStep;
        }
      }),
      this.tunnelDataStore.getLoading().subscribe(loading => {
        this.tunnelLoading = loading;
      }),
      this.tunnelDataStore.getPricingLoading().subscribe(pricingLoading => {
        this.pricingLoading = pricingLoading;
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),

      this.tunnelDataStore.getCurrentFunding().subscribe(funding => {
        if (funding) {
          this.currentFunding = funding;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.configuration = configurationData.get(
            this.customerService.context as CONFIG_CONTEXT
          ) as HBMConfigurationData;
        }
      }),
      combineLatest([this.tunnelNavigationStore.getNextStep(), this.tunnelDataStore.getCurrentState()]).subscribe(
        ([nextStep, state]) => {
          if (nextStep && state) {
            this.canNextStep = this.canTunnelNextStep(state);
          }
        }
      )
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.flyerVisible = false;
    }
  }

  protected async updateDiscount(newDiscount: DiscountTypeEnum): Promise<void> {
    this.flyerVisible = false;
    const requiredRecalculation =
      this.featureSettingsService.financementEnabled &&
      this.currentFunding?.downPayment != undefined &&
      this.currentPricing?.totalWithFunding != undefined;
    switch (this.customerService.context) {
      case CONFIG_CONTEXT.KITCHEN:
        await this.kitchenService.updateDiscount({
          tunnelConfigurationId: this.configuration.configurationId,
          discountType: newDiscount,
          requiresFinancingRecalculation: requiredRecalculation,
        });
        break;
      case CONFIG_CONTEXT.STORAGE:
        await this.storageService.updateDiscount({
          tunnelConfigurationId: this.configuration.configurationId,
          discountType: newDiscount,
          requiresFinancingRecalculation: requiredRecalculation,
        });
        break;
      case CONFIG_CONTEXT.BATHROOM_V2:
        await this.bathroomService.updateDiscount({
          tunnelConfigurationId: this.configuration.configurationId,
          discountType: newDiscount,
          requiresFinancingRecalculation: requiredRecalculation,
        });
        break;
      case CONFIG_CONTEXT.BATHROOM:
        await this.bathroomHbmService.updateDiscount({
          tunnelConfigurationId: this.configuration.configurationId,
          discountType: newDiscount,
          requiresFinancingRecalculation: requiredRecalculation,
        });
        break;
    }
  }

  private isSkipModalToOpen(step: TunnelStep) {
    if (!step.skipModalToOpen) return false;
    return step.skipModalToOpen(this.currentState, this.featureSettingsService.snapshot);
  }

  get isOrderSentToComPlus(): boolean {
    if (this.isCarpentry) {
      return hasCarpentryComPlusOrder(this.currentConfigurationData);
    }

    const data = this.currentConfigurationData as any;
    if (data?.opportunityNumber != undefined && data.opportunityNumber !== 0) {
      return true;
    }
    // Fallback: Check if any order has an opportunity number
    if (data?.orders?.length > 0) {
      return data.orders.some(o => o.opportunityNumber != null && o.opportunityNumber !== 0);
    }
    return false;
  }

  get isCarpentryOrderSent(): boolean {
    if (!this.isCarpentry) return false;
    return isCarpentryLockedBySapOrderNumber(this.currentConfigurationData);
  }

  get canUnlockProject(): boolean {
    return !this.isCarpentry || !this.isCarpentryOrderSent;
  }

  get orderEditionLoading(): boolean {
    return this.carpentryTunnelStore.orderEditionLoading;
  }

  async openCartOrderEdition(): Promise<void> {
    if (this.isCarpentry) {
      await this.carpentryTunnelStore.openOrderNumberEdition();
    }
  }

  protected async next() {
    if (this.beforeNext) {
      this.beforeNextProcessing = true;
      try {
        await this.beforeNext();
      } catch {
        // beforeNext a annulé la navigation (ex: fermeture de modale sans sélection)
        this.beforeNextProcessing = false;
        return;
      }
      this.beforeNextProcessing = false;
    }
    if (!(await this.tunnelNavigationStore.canNavigateToStep(this.nextStep.index))) return;

    if (this.nextStep.modalToOpen && !this.isSkipModalToOpen(this.nextStep)) {
      this.modalService.display(this.nextStep.modalToOpen, {
        index: this.nextStep.index,
      });
    } else {
      this.tunnelNavigationStore.navigateToStep(this.nextStep.index);
    }
  }

  protected previous() {
    if (this.previousStep.modalToOpen) {
      this.modalService.display(this.previousStep.modalToOpen, {
        index: this.previousStep.index,
      });
    } else {
      this.tunnelNavigationStore.previousStep();
    }
  }

  protected readonly DiscountTypeEnum = DiscountTypeEnum;

  protected async showProductList() {
    if (this.beforeProductList) {
      await this.beforeProductList();
    }

    if (this.isProductListDisabled) {
      if (this.isHbmProductListContext) {
        this.displayEmptyBomError();
      }
      return;
    }

    if (this.customerService.context === CONFIG_CONTEXT.CARPENTRY) {
      this.modalService.display(MODAL.CARPENTRY_PRODUCT_LIST, {
        isReadOnly: this.currentStep.readonlyProductList ?? false,
        bom: [],
      });
    } else {
      this.modalService.display(MODAL.PRODUCTS_LIST_V2, {
        bom: BomDataHelper.parseBomData(this.bom),
        reloadBom: false,
        isReadOnly: this.currentStep.readonlyProductList ?? false,
        isSave: true,
      });
    }
  }

  private displayEmptyBomError(): void {
    this.modalService.display(MODAL.MESSAGE, {
      titleRef: 'product-list.empty-bom-title',
      contentRef: BomDataHelper.hasUnavailablePrice(this.bom)
        ? 'product-list.unavailable-price-error'
        : 'product-list.empty-bom-error',
      buttons: [
        {
          labelRef: 'generic.close',
          primary: true,
          action: () => this.modalService.close(),
        },
      ],
    });
  }

  private canTunnelPrint(currentState: TunnelDataState) {
    if (this.forceSpecificPrintActionEnabled && this.specificPrintAction) {
      return true;
    }

    const poseEnabled = this.featureSettingsService.poseEnabled;
    const financementEnabled = this.featureSettingsService.financementEnabled;

    let canPrint =
      currentState.currentCustomer?.userData !== undefined &&
      (!financementEnabled ||
        (currentState.currentFunding !== undefined &&
          (currentState.currentFunding.fundingWithInstallation?.offerTable !== undefined ||
            currentState.currentFunding.fundingWithoutInstallation?.offerTable !== undefined))) &&
      currentState.currentDiscount !== undefined &&
      (!poseEnabled || currentState.currentInstallation?.withInstallation === false || currentState.currentInstallation?.detailInstallation !== undefined) &&
      currentState.currentPricing !== undefined &&
      (!financementEnabled || !currentState.fundingLoading) &&
      (!poseEnabled || !currentState.installationLoading);
    //Si fidélité est activé, il faut vérifier qu'on ai les info fid
    if (this.featureSettingsService.fidelityEnabled) {
      canPrint =
        canPrint &&
        currentState.currentCustomer.fidelity !== undefined &&
        currentState.currentPricing.fidelity?.length === 2;
    }
    return canPrint;
  }

  protected orderPrevisitKitchen() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    this.modalService.display(MODAL.FORM_POSE);
  }

  protected orderPrevisitStorage() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    this.modalService.display(MODAL.FORM_POSE);
  }

  protected orderPrevisitBathroomHbm() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    this.modalService.display(MODAL.FORM_POSE);
  }

  protected orderPrevisitBathroom() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    this.modalService.display(MODAL.FORM_POSE);
  }

  protected orderPrevisitCarpentry() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    this.modalService.display(MODAL.FORM_POSE);
  }

  protected async orderPrevisitWorkbench() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    this.comPlusLoading = true;
    let xml: string;
    try {
      xml = await this.workbenchService.getPrevisit(
        (this.currentConfigurationData as KitchenConfigurationData).workbenchConfigurationId
      );
    } catch (error: any) {
      this.comPlusLoading = false;
      alert(
        this.translateService.instant('popins.validation.com_plus_error.previsit_create_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }

    let workbenchPrevisitOpportunityNumber;
    try {
      workbenchPrevisitOpportunityNumber = await this.processQuoteService.sendDotComPlusOrder(xml);
    } catch (error: any) {
      this.comPlusLoading = false;
      alert(
        this.translateService.instant('validation.kitchen.previsit_send_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }

    this.comPlusLoading = false;
    alert(
      this.translateService.instant('validation.kitchen.workbench_previsit_sent', {
        opportunityNumber: workbenchPrevisitOpportunityNumber,
      })
    );
  }

  private canTunnelNextStep(currentState: TunnelDataState): boolean {
    if (this.nextStep === undefined) {
      return false;
    }
    return !(this.nextStep.isDisabled?.(currentState, this.featureSettingsService.snapshot) ?? false);
  }

  protected isPreviousStepDisabled(): boolean {
    return this.previousStep?.isDisabled?.(this.currentState, this.featureSettingsService.snapshot) ?? false;
  }

  private buildHbmPdfViewModel(): HbmPdfViewModel {
    return {
      tunnelConfigurationId:
        this.customerService.context === CONFIG_CONTEXT.CARPENTRY
          ? (this.currentConfigurationData as CarpentryConfigurationData).tunnelId
          : this.currentConfigurationData.configurationId,
      projectImageUrl: [this.currentConfigurationData?.medias?.thumbnail[0]?.url],
      plan2DImageUrl: this.currentConfigurationData?.medias?.plan2D?.map(p => p.url) ?? [],
      fidelityCardPoints: this.featureSettingsService.fidelityEnabled
        ? this.currentState.currentPricing.fidelity
        : [],
      sortDirections:
        this.customerService.context === CONFIG_CONTEXT.CARPENTRY
          ? this.carpentryTunnelStore.sortDirections
          : undefined,
    };
  }

  protected get featureWarningMessages(): string[] {
    const messages: string[] = [];

    if (!this.featureSettingsService.fidelityEnabled) {
      messages.push(this.translateService.instant('feature-settings.warning.fidelity'));
    }

    if (!this.featureSettingsService.poseEnabled) {
      messages.push(this.translateService.instant('feature-settings.warning.pose'));
    }

    if (!this.featureSettingsService.financementEnabled) {
      messages.push(this.translateService.instant('feature-settings.warning.financement'));
    }

    return messages;
  }

  protected async print() {
    if (!this.customerService.userDatas) {
      this.identifyCustomer(() => this.print());
      return;
    }

    try {
      this.printLoading = true;
      if (this.beforeNext) {
        await this.beforeNext();
      }
      if (this.specificPrintAction) {
        await this.specificPrintAction();
      } else {
        let hbmpdfViewModel: HbmPdfViewModel = this.buildHbmPdfViewModel();
        switch (this.customerService.context) {
          case CONFIG_CONTEXT.KITCHEN:
            await this.printKitchen(hbmpdfViewModel);
            break;
          case CONFIG_CONTEXT.STORAGE:
            await this.printStorage(hbmpdfViewModel);
            break;
          case CONFIG_CONTEXT.BATHROOM_V2:
            await this.printBathroom(hbmpdfViewModel);
            break;
          case CONFIG_CONTEXT.BATHROOM:
            await this.printBathroomHbm(hbmpdfViewModel);
            break;
          case CONFIG_CONTEXT.CARPENTRY:
            await this.printCarpentry(hbmpdfViewModel);
            break;
        }
      }
    } catch (error) {
      logError(error);
      this.printLoading = false;
    } finally {
      this.printLoading = false;
    }
  }

  private async printKitchen(hbmPdfViewModel: HbmPdfViewModel) {
    try {
      const base64 = await this.kitchenService.generateKitchenPdf(hbmPdfViewModel);
      this.printService.printPdf(base64.value, true);
    } catch (error) {
      logError(error);
    }
  }

  private async printStorage(hbmPdfViewModel: HbmPdfViewModel) {
    try {
      const base64 = await this.storageService.generateStoragePdf(hbmPdfViewModel);
      this.printService.printPdf(base64.value, true);
    } catch (error) {
      logError(error);
    }
  }

  protected async sendEmail() {
    if (!this.customerService.userDatas) {
      this.identifyCustomer(() => this.sendEmail());
      return;
    }

    try {
      this.printLoading = true;
      if (this.beforeNext) {
        await this.beforeNext();
      }
      const pdfViewModel: HbmPdfViewModel = this.buildHbmPdfViewModel();
      let service: BaseAppService<Configuration2D>;

      switch (this.customerService.context) {
        case CONFIG_CONTEXT.KITCHEN:
          service = this.kitchenService;
          break;
        case CONFIG_CONTEXT.STORAGE:
          service = this.storageService;
          break;
        case CONFIG_CONTEXT.CARPENTRY:
          service = this.carpentryService;
          break;
        default:
          throw new Error(`Impossible d'envoyer un mail pour le contexte ${this.customerService.context}`);
      }

      await service.sendMailWithPdf(
        pdfViewModel.tunnelConfigurationId,
        pdfViewModel,
        this.currentState.currentCustomer.userData.email,
        this.customerService.context
      );

      this.toastService.showSuccessToast('Mail envoyé', 'Le mail a été envoyé avec succès');
    } catch (error) {
      console.error(error);
      this.toastService.showErrorToast('Erreur envoi mail', "Le mail n'a pas pu être envoyé");
    } finally {
      this.printLoading = false;
    }
  }

  private async printBathroom(hbmPdfViewModel: HbmPdfViewModel) {
    try {
      const base64 = await this.bathroomService.generateBathroomPdf(hbmPdfViewModel);
      this.printService.printPdf(base64.value, true);
    } catch (error) {
      logError(error);
    }
  }

  private async printBathroomHbm(hbmPdfViewModel: HbmPdfViewModel) {
    try {
      const base64 = await this.bathroomHbmService.generateBathroomHbmPdf(hbmPdfViewModel);
      this.printService.printPdf(base64.value, true);
    } catch (error) {
      logError(error);
    }
  }

  private async printCarpentry(hbmPdfViewModel: HbmPdfViewModel) {
    try {
      const base64 = await this.carpentryService.generateCarpentryPdf(hbmPdfViewModel);
      this.printService.printPdf(base64.value, true);
    } catch (error) {
      logError(error);
    }
  }

  private async generateXML(): Promise<{ xml: string; orderId?: string; contactedByPhone?: boolean }> {
    let xml = '';
    let orderId: string | undefined;
    let contactedByPhone: boolean | undefined;
    if (this.customerService.context == CONFIG_CONTEXT.KITCHEN) {
      const id = (this.currentConfigurationData as KitchenConfigurationData).kitchenConfigurationId;
      const res = await this.kitchenService.generateXML(id);
      xml = res.xml;
      orderId = res.orderId;
      contactedByPhone = res.contactedByPhone;
    } else if (this.customerService.context == CONFIG_CONTEXT.STORAGE) {
      const id = (this.currentConfigurationData as StorageConfigurationData).storageConfigurationId;
      const res = await this.storageService.generateXML(id);
      xml = res.xml;
      orderId = res.orderId;
    } else if (this.customerService.context == CONFIG_CONTEXT.BATHROOM_V2) {
      const id = (this.currentConfigurationData as BathroomConfigurationData).bathroomConfigurationId;
      const res = await this.bathroomService.generateXML(id);
      xml = res.xml;
      orderId = res.orderId;
    } else if (this.customerService.context == CONFIG_CONTEXT.CARPENTRY) {
      const id = (this.currentConfigurationData as CarpentryConfigurationData).id;
      const res = await this.carpentryService.generateXML(id);
      xml = res.xml;
      orderId = res.orderId;
    } else if (this.customerService.context == CONFIG_CONTEXT.BATHROOM) {
      const id = (this.currentConfigurationData as BathroomHbmConfigurationData).bathroomHbmConfigurationId;
      const res = await this.bathroomHbmService.generateXML(id);
      xml = res.xml;
      orderId = res.orderId;
    }

    return { xml, orderId, contactedByPhone };
  }

  private async sendHBMComPlus(xml: string): Promise<number> {
    let opportunityNumber;
    try {
      opportunityNumber = await this.processQuoteService.sendDotComPlusOrder(xml);
      return opportunityNumber;
    } catch (error: any) {
      throw this.translateService.instant('validation.com_plus_error.kitchen_send_error', {
        error: JSON.stringify(error),
      });
    }
  }

  private async updateKitchenOpportunityNumber(opportunityNumber: number) {
    await this.kitchenService.updateKitchenConfiguration(
      {
        opportunityNumber: opportunityNumber,
      },
      false
    );
  }

  private async updateStorageOpportunityNumber(opportunityNumber: number) {
    await this.storageService.updateStorageConfiguration(
      {
        opportunityNumber: opportunityNumber,
      },
      false
    );
  }

  private async updateBathroomOpportunityNumber(opportunityNumber: number) {
    await this.bathroomService.updateBathroomConfiguration(
      {
        opportunityNumber: opportunityNumber,
      },
      false
    );
  }

  private async updateCarpentryOpportunityNumber(opportunityNumber: number) {
    await this.carpentryService.updateCarpentryConfiguration(
      {
        opportunityNumber: opportunityNumber,
      },
      false
    );
  }

  private async updateKitchenWorkbenchOpportunityNumber(opportunityNumber: number) {
    await this.kitchenService.updateKitchenConfiguration(
      {
        workbenchOpportunityNumber: opportunityNumber,
      },
      false
    );
  }

  private async sendWorkbenchConfigToCOMPlus(contactedByPhone?: boolean) {
    if ((this.currentConfigurationData as KitchenConfigurationData).workbenchConfigurationId) {
      let res: { orderId: string; xml: string };
      try {
        res = await this.workbenchService.generateXML(
          (this.currentConfigurationData as KitchenConfigurationData).workbenchConfigurationId,
          contactedByPhone
        );
      } catch (error: any) {
        throw new Error(
          this.translateService.instant('validation.com_plus_error.workbench_create_error', {
            error: JSON.stringify(error),
          })
        );
      }

      try {
        const workbenchOpportunityNumber: number = await this.processQuoteService.sendDotComPlusOrder(res.xml);
        //Dans le cas ou on veut envoyer à com+ et qu'il y a pas de config de chargée (skip config)
        if (!this.workbenchService.configuration?.id) {
          await this.workbenchService.getProjectById(
            (this.currentConfigurationData as KitchenConfigurationData).workbenchConfigurationId,
            true
          );
        }
        await this.workbenchService.updateOrderAndSaveProject(res.orderId, workbenchOpportunityNumber);
        return workbenchOpportunityNumber;
      } catch (error: any) {
        throw new Error(
          this.translateService.instant('validation.com_plus_error.workbench_send_error', {
            error: JSON.stringify(error),
          })
        );
      }
    }
  }

  protected async orderPrevisit() {
    this.comPlusLoading = false;
    let xml: string;
    try {
      xml = await this.workbenchService.getPrevisit(
        (this.currentConfigurationData as KitchenConfigurationData).workbenchConfigurationId
      );
    } catch (error: any) {
      this.comPlusLoading = false;
      alert(
        this.translateService.instant('validation.com_plus_error.previsit_create_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }
    let workbenchPrevisitOpportunityNumber;
    try {
      workbenchPrevisitOpportunityNumber = await this.processQuoteService.sendDotComPlusOrder(xml);
    } catch (error: any) {
      this.comPlusLoading = false;
      alert(
        this.translateService.instant('validation.com_plus_error.previsit_send_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }
    this.comPlusLoading = false;
    alert(
      this.translateService.instant('validation.previsit_sent', {
        opportunityNumber: workbenchPrevisitOpportunityNumber,
      })
    );
  }

  protected async unlockProject() {
    if (!this.canUnlockProject) {
      return;
    }

    try {
      this.unlockLoading = true;

      switch (this.customerService.context) {
        case CONFIG_CONTEXT.BATHROOM_V2:
          await this.bathroomService.lockUnlockBathroomConfiguration();
          break;
        case CONFIG_CONTEXT.CARPENTRY:
          await this.carpentryService.updateLockState(false);
          break;
        default:
          await this.projectService.unlock(this.currentService.HBMProject.id);
          this.currentService.HBMProject.locked = false;
          break;
      }
    } catch (e) {
      logError(e);
    } finally {
      this.unlockLoading = false;
    }
  }

  private async processComPlusByContext(opportunityNumber: number, orderId?: string, contactedByPhone?: boolean) {
    switch (this.customerService.context) {
      case CONFIG_CONTEXT.KITCHEN:
        return await this.processKitchenComPlus(opportunityNumber, orderId, contactedByPhone);
      case CONFIG_CONTEXT.STORAGE:
        return await this.processStorageComPlus(opportunityNumber, orderId);
      case CONFIG_CONTEXT.BATHROOM_V2:
        return await this.processBathroomComPlus(opportunityNumber, orderId);
      case CONFIG_CONTEXT.BATHROOM:
        return await this.processBathroomHbmComPlus(opportunityNumber, orderId);
      case CONFIG_CONTEXT.CARPENTRY:
        return await this.processCarpentryComPlus(opportunityNumber, orderId);
    }
  }

  private async processBathroomHbmComPlus(opportunityNumber: number, orderId?: string) {
    this.updateBathroomHbmOpportunityNumber(opportunityNumber);
    if (orderId) {
      await this.bathroomHbmService.saveOpportunityNumber(orderId, opportunityNumber);
    }
    return {
      opportunityNumber: opportunityNumber,
    };
  }

  /** Reflète le n° d'opportunité dans le store SDB HBM. */
  private updateBathroomHbmOpportunityNumber(opportunityNumber: number) {
    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM, {
      ...(this.currentConfigurationData as BathroomHbmConfigurationData),
      opportunityNumber,
    });
  }

  private async processCarpentryComPlus(opportunityNumber: number, orderId?: string) {
    await this.updateCarpentryOpportunityNumber(opportunityNumber);
    if (orderId) {
      await this.carpentryService.updateOrderAndSaveProject(orderId, opportunityNumber);
      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.CARPENTRY, this.carpentryService.configuration);
    }
    return {
      opportunityNumber: opportunityNumber,
    };
  }

  /**
   * Délègue le contrôle pré-envoi au service du configurateur courant.
   * L'action bar ne porte pas la règle métier : elle orchestre seulement
   * le blocage éventuel et l'affichage de la modale retournée par le service.
   */
  private async checkOrderBeforeSend(): Promise<boolean> {
    try {
      const service = this.serviceManager.getService(this.customerService.context) as BaseAppService<Configuration2D>;
      const checkResult = await service?.checkOrderBeforeSend();

      if (checkResult?.isValid === false) {
        if (checkResult.modalId) {
          this.modalService.display(checkResult.modalId, {
            ...checkResult.modalData,
            onClose: () => this.modalService.close(),
          });
        }

        return false;
      }

      return true;
    } catch (error) {
      logError(error);
      return false;
    }
  }

  private async processStorageComPlus(opportunityNumber: number, orderId?: string) {
    await this.updateStorageOpportunityNumber(opportunityNumber);
    if (orderId) {
      await this.storageService.saveOpportunityNumber(orderId, opportunityNumber);
    }
    return {
      opportunityNumber: opportunityNumber,
    };
  }

  private async processBathroomComPlus(opportunityNumber: number, orderId?: string) {
    await this.updateBathroomOpportunityNumber(opportunityNumber);
    if (orderId) {
      await this.bathroomService.saveOpportunityNumber(orderId, opportunityNumber);
    }
    return {
      opportunityNumber: opportunityNumber,
    };
  }

  private async processKitchenComPlus(opportunityNumber: number, orderId?: string, contactedByPhone?: boolean) {
    await this.updateKitchenOpportunityNumber(opportunityNumber);

    let workbenchOpportunityNumber;
    if ((this.currentConfigurationData as KitchenConfigurationData).workbenchConfigurationId) {
      try {
        workbenchOpportunityNumber = await this.sendWorkbenchConfigToCOMPlus(contactedByPhone);
      } catch (error: any) {
        logError(error);
        this.comPlusLoading = false;
        return;
      }
    }

    await this.updateKitchenWorkbenchOpportunityNumber(workbenchOpportunityNumber);
    if (orderId) {
      await this.kitchenService.saveOpportunityNumber(orderId, opportunityNumber);
    }
    return {
      opportunityNumber: opportunityNumber,
      workbenchOpportunityNumber: workbenchOpportunityNumber,
    };
  }

  protected async sendComPlus() {
    if (this.customerService.context === CONFIG_CONTEXT.BASKET) {
      await this.sendBasketComPlus();
      return;
    }

    this.comPlusLoading = true;

    const canSend = await this.checkOrderBeforeSend();
    if (!canSend) {
      this.comPlusLoading = false;
      return;
    }

    const {
      xml: _dotComPlusProcessQuoteXml,
      orderId,
      contactedByPhone,
    } = await this.generateXML();

    let opportunityNumber: number;
    try {
      opportunityNumber = await this.sendHBMComPlus(_dotComPlusProcessQuoteXml);
    } catch (error) {
      logError(error);
      this.comPlusLoading = false;
      return;
    }

    let opportunityNumbers = await this.processComPlusByContext(opportunityNumber, orderId, contactedByPhone);

    switch (this.customerService.context) {
      case CONFIG_CONTEXT.BATHROOM_V2: // Pour SDB, comme on ne passe pas par HBM, on lock le projet en local dans la BDD
        this.bathroomService.lockUnlockBathroomConfiguration();
        break;
      case CONFIG_CONTEXT.CARPENTRY:
        await this.carpentryService.updateLockState(true);
        break;
      default: // Par défaut, on lock le projet dans HBM
        try {
          await this.projectService.lock(this.currentService.HBMProject.uuid);
          this.currentService.HBMProject.locked = true;
        } catch (error) {
          logError(error);
        }
        break;
    }

    this.comPlusLoading = false;
    this.modalService.display(MODAL.ORDER_CONFIRM, {
      ...opportunityNumbers,
    });
  }

  /** Envoie la liste d'achat à COM+ et affiche le numéro d'opportunité obtenu. */
  private async sendBasketComPlus(): Promise<void> {
    if (!this.customerService.userDatas) {
      this.identifyCustomer(() => this.sendBasketComPlus());
      return;
    }

    try {
      this.comPlusLoading = true;
      const opportunityNumber = await this.basketService.saveProjectAndGetOpportunityNumber();

      if (opportunityNumber <= 0) {
        this.toastService.showErrorToast(
          "Erreur lors de l'envoi",
          this.translateService.instant('purchase-list.error-failed-to-send-to-com')
        );
        return;
      }

      this.modalService.display(MODAL.ORDER_CONFIRM, { opportunityNumber: opportunityNumber });
    } finally {
      this.comPlusLoading = false;
    }
  }

  /** Ouvre la recherche client, puis exécute l'action une fois le client sélectionné. */
  private identifyCustomer(afterIdentification: () => Promise<void>): void {
    this.modalService.display(MODAL.SEARCH_CUSTOMER, {
      configContext: this.customerService.context,
      onCustomerSelect: async () => {
        this.modalService.close(true);
        await afterIdentification();
      },
    });
  }

  /** Enregistre le projet puis envoie le devis par mail au client identifié. */
  protected async sendQuoteByMail(): Promise<void> {
    if (!this.customerService.userDatas) {
      this.identifyCustomer(() => this.sendQuoteByMail());
      return;
    }

    try {
      this.sendMailLoading = true;
      await this.basketService.saveProject();
      await this.basketService.sendToMail();
      this.toastService.showSuccessToast('Devis envoyé', 'Le devis a été envoyé par mail au client.');
    } catch (error) {
      logError(error);
      this.toastService.showErrorToast("Erreur lors de l'envoi", `L'envoi du devis par mail a échoué : ${error}`);
    } finally {
      this.sendMailLoading = false;
    }
  }

  protected async save() {
    if (this.customerService.context === CONFIG_CONTEXT.BASKET && !this.customerService.userDatas) {
      this.identifyCustomer(() => this.save());
      return;
    }

    try {
      this.saveLoading = true;
      // await this.kitchenService.updateKitchen();
      if (this.customerService.context === CONFIG_CONTEXT.CARPENTRY) {
        await this.carpentryService.saveProject();
      } else if (this.customerService.context === CONFIG_CONTEXT.BATHROOM) {
        await this.bathroomHbmService.save();
      }

      if (this.customerService.context === CONFIG_CONTEXT.BASKET) {
        await this.basketService.saveProject();
      }

      this.statisticService.sendStatistic(
        StatisticTypeEnum.SAVING_COUNT,
        1,
        this.customerService.context,
        this.configuration.configurationId
      );
      this.toastService.showSuccessToast('Enregistrement effectué', "L'enregistrement a correctement été effectué");
    } catch (error: any) {
      logError(error);
      this.toastService.showErrorToast("Erreur lors de l'enregistrement", `L'enregistrement a échoué : ${error}`);
    } finally {
      this.saveLoading = false;
    }
  }

  protected async addProduct() {
    if (this.customerService.context === CONFIG_CONTEXT.CARPENTRY) {
      this.carpentryTunnelStore.addProduct();
      return;
    }

    if (this.isBathroomV2) {
      this.modalService.display(MODAL.BATHROOM_PRODUCT_SEARCH);
    }
  }

  protected async saveWithoutNavigation() {
    if (this.beforeNext) {
      this.beforeNextProcessing = true;
      try {
        await this.beforeNext();
      } catch {
        this.beforeNextProcessing = false;
        return;
      }
      this.beforeNextProcessing = false;
    }

    // Vérifie si on peut continuer (mêmes règles que next)
    if (!(await this.tunnelNavigationStore.canNavigateToStep(this.nextStep.index))) {
      return;
    }

    // Si une modale doit s’ouvrir, on l’ouvre mais on NE NAVIGUE PAS
    if (this.nextStep.modalToOpen && !this.isSkipModalToOpen(this.nextStep)) {
      this.modalService.display(this.nextStep.modalToOpen, {
        index: this.nextStep.index,
      });
      return;
    }

    // Ici on NE NAVIGUE PAS
    // On appelle juste save()
    await this.save();
  }


  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faPercentage = faPercentage;
  protected readonly faPen = faPen;
  protected readonly faPrint = faPrint;
  protected readonly faList = faList;
  protected readonly faArrowRight = faArrowRight;
  protected readonly faPlus = faPlus;
  protected readonly faUnlock = faUnlock;
  protected readonly faSave = faSave;
  protected readonly faPaperPlane = faPaperPlane;
  protected readonly faEnvelope = faEnvelope;
}
